import styled from "styled-components"
import useHomeworkStudentView from "features/homework/hooks/useHomeworkStudentView"
import PLAY_SVG from "assets/images/play.svg";
import HomeworkReportModal from "./HomeworkReportModal";
import { report, student } from "shared/types/Homework";

interface HomeworkStudentViewProps{
    selectedReport:report,
    selectedLesson:string,
    selectedStudent:student | undefined,
    onStudentClickHandler:(student:student | undefined) => void,
}

const HomeworkStudentView = ({selectedReport, selectedLesson, onStudentClickHandler}:HomeworkStudentViewProps) => {
    const {
        studentList,
        isOpenLoadingModal,
        loadingProgress,
        searchKeyword,
        sortOrder,
        sortBy,
        handleSearch,
        toggleSortOrder,
        toggleSortBy
    } = useHomeworkStudentView(selectedLesson, selectedReport);

    return (
        <Container>
            <SearchContainer>
                <SearchWrapper>
                    <SearchIcon>🔍</SearchIcon>
                    <SearchInput 
                        type="text"
                        placeholder="학생 이름 검색..."
                        value={searchKeyword}
                        onChange={(e) => handleSearch(e.target.value)}
                    />
                </SearchWrapper>
                <SortButtonGroup>
                    <SortButton 
                        onClick={() => toggleSortBy('name')}
                        active={sortBy === 'name'}
                    >
                        이름순
                    </SortButton>
                    <SortButton 
                        onClick={() => toggleSortBy('progress')}
                        active={sortBy === 'progress'}
                    >
                        진도순
                    </SortButton>
                    <SortButton onClick={toggleSortOrder}>
                        {sortOrder === 'asc' ? '↑ 오름차순' : '↓ 내림차순'}
                    </SortButton>
                </SortButtonGroup>
            </SearchContainer>
            <Table>
                <TableHeader>
                    <TableColumn style={{width:"45%"}}>이름</TableColumn>
                    <TableColumn style={{width:"35%"}}>과제 수행율</TableColumn>
                    <TableColumn style={{width:"20%"}}>자세히 보기</TableColumn>
                </TableHeader>
                <TableBody>
                    {isOpenLoadingModal ? (
                        <LoadingContainer>
                            <LoadingSpinner />
                            <LoadingText>학생 목록을 불러오는 중... {Math.round(loadingProgress)}%</LoadingText>
                        </LoadingContainer>
                    ) : studentList.length === 0 ? (
                        <EmptyContainer>
                            <EmptyText>학생이 없습니다</EmptyText>
                        </EmptyContainer>
                    ) : (
                        studentList.map((item) => (
                            <TableRow key={item.uid}>
                                <TableColumn style={{width:"45%"}}>{item.name}</TableColumn>
                                <TableColumn style={{width:"35%"}}>
                                    <ProgressWrap>
                                        <ProgressBar progress={item.progress}/>
                                        <Progress>{item.progress}%</Progress>
                                    </ProgressWrap>
                                </TableColumn>
                                <TableColumn style={{width:"20%"}}>
                                    <DetailButton 
                                        src={PLAY_SVG} 
                                        alt="detail button" 
                                        onClick={() => onStudentClickHandler(item)}
                                    />
                                </TableColumn>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    padding: 20px;
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 16px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05), 0 8px 24px rgba(15, 23, 42, 0.04);
    min-height: 0;
`

const Table = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    flex-grow: 1;
    background: #FFFFFF;
    border-radius: 8px;
    overflow: hidden;
`

const TableHeader = styled.div`
    display: flex;
    background: #F8FAFC;
    border-bottom: 2px solid #E2E8F0;
    position: sticky;
    top: 0;
    z-index: 1;
`

const TableColumn = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 12px 16px;
    font-size: 14px;
    color: #64748B;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const TableBody = styled.div`
    flex: 1;
    min-height: 0;
    overflow: auto;
    
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: #F1F5F9;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #CBD5E1;
        border-radius: 3px;
    }
`

const TableRow = styled.div<{selected?:boolean}>`
    display: flex;
    border-bottom: 1px solid #E2E8F0;
    background-color: ${(props) => props.selected ? "#FEF2F2" : "#FFFFFF"};
    transition: all 0.2s ease;
    
    &:hover {
        background-color: #F8FAFC;
    }
    
    ${TableColumn} {
        color: #1E293B;
        font-weight: ${(props) => props.selected ? 600 : 400};
    }
`

const ProgressWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 0 8px;
`

const getColor = (props:any) => {
    if (props.progress >= 70) {
      return '#10B981';
    } else if (props.progress <= 30) {
      return '#EF4444';
    } else {
      return '#F59E0B';
    }
};

const ProgressBar = styled.div<{progress:number}>`
    flex-grow: 1;
    height: 6px;
    background: #E2E8F0;
    border-radius: 3px;
    overflow: hidden;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: ${props => props.progress}%;
        background: ${props => getColor(props)};
        border-radius: 3px;
        transition: width 0.3s ease;
    }
`

const Progress = styled.span`
    font-size: 12px;
    color: #64748B;
    font-weight: 500;
    min-width: 40px;
    text-align: right;
`

const DetailButton = styled.img`
    width: 24px;
    height: 24px;
    padding: 4px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
        background-color: #F1F5F9;
        transform: scale(1.1);
    }
    
    &:active {
        transform: scale(0.95);
    }
`

const SearchContainer = styled.div`
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
`

const SearchWrapper = styled.div`
    flex: 1;
    position: relative;
    display: flex;
    align-items: center;
`

const SearchIcon = styled.span`
    position: absolute;
    left: 12px;
    color: #94A3B8;
    font-size: 14px;
`

const SearchInput = styled.input`
    width: 100%;
    padding: 10px 12px 10px 36px;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    font-size: 14px;
    color: #1E293B;
    background: #F8FAFC;
    transition: all 0.2s ease;

    &:focus {
        outline: none;
        border-color: #DF0000;
        background: #FFFFFF;
        box-shadow: 0 0 0 3px rgba(223, 0, 0, 0.1);
    }

    &::placeholder {
        color: #94A3B8;
    }
`

const SortButtonGroup = styled.div`
    display: flex;
    gap: 8px;
`

const SortButton = styled.button<{active?: boolean}>`
    padding: 10px 16px;
    background: ${props => props.active ? '#DF0000' : '#F8FAFC'};
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    color: ${props => props.active ? '#FFFFFF' : '#64748B'};
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        background: ${props => props.active ? '#B91C1C' : '#F1F5F9'};
        border-color: #CBD5E1;
    }

    &:active {
        transform: scale(0.98);
    }
`

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 16px;
`

const LoadingSpinner = styled.div`
    width: 40px;
    height: 40px;
    border: 3px solid #E2E8F0;
    border-top: 3px solid #DF0000;
    border-radius: 50%;
    animation: spin 1s linear infinite;

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`

const LoadingText = styled.div`
    color: #64748B;
    font-size: 14px;
    font-weight: 500;
`

const EmptyContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
`

const EmptyText = styled.div`
    color: #64748B;
    font-size: 14px;
    font-weight: 500;
`

export default HomeworkStudentView;